import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Clock } from "lucide-react";

export function ContactSection() {
  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Contact <span className="text-orange-400">AdHub</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-orange-700 mx-auto mb-8"></div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Contact us to receive a detailed proposal within 48 hours
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div>
              <h3 className="text-2xl font-bold mb-6 text-white">
                Contact Information
              </h3>
              
              {/* Contact Person */}
              <Card className="bg-gray-800/50 border-gray-700 mb-6">
                <CardHeader>
                  <CardTitle className="text-xl text-orange-400">
                    Trúc Anh
                  </CardTitle>
                  <p className="text-gray-300 mb-4">Head of Growth</p>
                </CardHeader>
              </Card>

              {/* Contact Details */}
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <div className="bg-orange-500/20 p-3 rounded-full">
                    <Mail className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Email</p>
                    <p className="text-white">contact@adhub.vn</p>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="bg-orange-600/20 p-3 rounded-full">
                    <Phone className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Phone</p>
                    <p className="text-white">0935.662.136</p>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="bg-orange-700/20 p-3 rounded-full">
                    <MapPin className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Address</p>
                    <p className="text-white">128/3/21 Lê Văn Duyệt, Gia Định, TP Hồ Chí Minh, Việt Nam</p>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="bg-orange-800/20 p-3 rounded-full">
                    <Clock className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Business Hours</p>
                    <p className="text-white">Mon-Fri: 9:00 AM - 6:00 PM</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Map Preview */}
            <Card className="bg-gray-800/50 border-gray-700">
              <CardContent className="p-4">
                <div className="bg-gray-700 rounded-lg h-48 flex items-center justify-center">
                  <div className="text-center text-gray-400">
                    <MapPin className="w-12 h-12 mx-auto mb-2" />
                    <p>Interactive Map</p>
                    <p className="text-sm">128/3/21 Lê Văn Duyệt, Gia Định, TP Hồ Chí Minh, Việt Nam</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <Card className="bg-gray-800/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-2xl text-white">
                  Request Consultation
                </CardTitle>
                <p className="text-gray-300">
                  Leave your information, we'll get back to you within 24 hours
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-white">Full Name *</Label>
                    <Input 
                      id="name" 
                      className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400 mt-2"
                      placeholder="Enter your name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="company" className="text-white">Company</Label>
                    <Input 
                      id="company" 
                      className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400 mt-2"
                      placeholder="Company name"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email" className="text-white">Email *</Label>
                    <Input 
                      id="email" 
                      type="email"
                      className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400 mt-2"
                      placeholder="email@example.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-white">Phone *</Label>
                    <Input 
                      id="phone" 
                      className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400 mt-2"
                      placeholder="0901 234 567"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="service" className="text-white">Service of Interest</Label>
                  <div className="relative mt-2">
                    <select 
                      id="service"
                      className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:bg-gray-600"
                    >
                      <option value="">Select service</option>
                      <option value="digital">Digital Marketing</option>
                      <option value="offline">Offline & Activation</option>
                      <option value="tech">Tech & Content Solutions</option>
                      <option value="all">All Services</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="message" className="text-white">Project Requirements</Label>
                  <Textarea 
                    id="message"
                    className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400 min-h-[120px] mt-2"
                    placeholder="Describe your project needs and objectives in detail..."
                  />
                </div>

                <Button 
                  className="w-full bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-lg py-6"
                  size="lg"
                >
                  Submit Consultation Request
                </Button>

                <div className="text-center">
                  <p className="text-gray-400 text-sm">
                    By submitting this form, you agree to AdHub contacting you for consultation
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
}